import { Injectable, inject, signal } from '@angular/core';
import Keycloak from 'keycloak-js';
import { authConfig } from './auth.config';
import { MeSessionService } from './me-session.service';

export type UserProfile = {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
  roles: string[];
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private isReauthenticating = false;
  private readonly meSessionService = inject(MeSessionService);

  private readonly keycloak = new Keycloak({
    url: authConfig.url,
    realm: authConfig.realm,
    clientId: authConfig.clientId
  });

  protected readonly profileState = signal<UserProfile | null>(null);
  protected readonly initializedState = signal(false);
  protected readonly authenticatedState = signal(false);

  async init(): Promise<void> {
    const authenticated = await this.keycloak.init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      checkLoginIframe: false
    });

    if (!authenticated) {
      await this.keycloak.login({ redirectUri: window.location.href, locale: 'fr' });
      return;
    }

    this.authenticatedState.set(authenticated);
    this.profileState.set(this.readProfileFromToken());
    this.initializedState.set(true);
  }

  get initialized(): boolean {
    return this.initializedState();
  }

  get authenticated(): boolean {
    return this.authenticatedState();
  }

  get profile(): UserProfile | null {
    return this.profileState();
  }

  get token(): string | undefined {
    return this.keycloak.token;
  }

  get roles(): string[] {
    return this.profileState()?.roles ?? [];
  }

  hasRole(role: string): boolean {
    const expected = role.trim().toUpperCase();
    return this.roles.some((currentRole) => currentRole.trim().toUpperCase() === expected);
  }

  hasAnyRole(...roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  async updateToken(minValidity = 30): Promise<boolean> {
    if (!this.authenticatedState()) {
      return false;
    }
    return this.keycloak.updateToken(minValidity);
  }

  get tokenExpiresAt(): number | null {
    const exp = this.keycloak.tokenParsed?.exp;
    if (typeof exp !== 'number') {
      return null;
    }
    return exp * 1000;
  }

  get remainingTokenLifetimeMs(): number {
    const expiresAt = this.tokenExpiresAt;
    if (!expiresAt) {
      return 0;
    }
    return Math.max(0, expiresAt - Date.now());
  }

  async login(): Promise<void> {
    await this.keycloak.login({ redirectUri: window.location.href, locale: 'fr' });
  }

  async logout(): Promise<void> {
    this.meSessionService.clear();
    await this.keycloak.logout({
      redirectUri: window.location.origin
    });
  }

  async reauthenticate(): Promise<void> {
    if (this.isReauthenticating) {
      return;
    }
    this.isReauthenticating = true;
    await this.keycloak.login({
      redirectUri: window.location.href,
      locale: 'fr'
    });
  }

  private readProfileFromToken(): UserProfile | null {
    const parsedToken = this.keycloak.tokenParsed;
    if (!parsedToken) {
      return null;
    }

    const firstName = String(parsedToken['given_name'] ?? '');
    const lastName = String(parsedToken['family_name'] ?? '');
    const fullName =
      String(parsedToken['name'] ?? '').trim() ||
      `${firstName} ${lastName}`.trim();

    return {
      id: String(parsedToken['sub'] ?? ''),
      username: String(parsedToken['preferred_username'] ?? ''),
      firstName,
      lastName,
      email: String(parsedToken['email'] ?? ''),
      fullName,
      roles: this.extractRolesFromToken(parsedToken)
    };
  }

  private extractRolesFromToken(parsedToken: Record<string, unknown>): string[] {
    const roles = new Set<string>();

    const realmAccess = parsedToken['realm_access'];
    if (realmAccess && typeof realmAccess === 'object' && 'roles' in realmAccess) {
      const realmRoles = (realmAccess as { roles?: unknown }).roles;
      if (Array.isArray(realmRoles)) {
        for (const role of realmRoles) {
          if (typeof role === 'string' && role.trim()) {
            roles.add(role.trim());
          }
        }
      }
    }

    const resourceAccess = parsedToken['resource_access'];
    if (resourceAccess && typeof resourceAccess === 'object') {
      for (const value of Object.values(resourceAccess as Record<string, unknown>)) {
        if (!value || typeof value !== 'object' || !('roles' in value)) {
          continue;
        }
        const clientRoles = (value as { roles?: unknown }).roles;
        if (!Array.isArray(clientRoles)) {
          continue;
        }
        for (const role of clientRoles) {
          if (typeof role === 'string' && role.trim()) {
            roles.add(role.trim());
          }
        }
      }
    }

    return [...roles];
  }
}
